import React from 'react'
import { observer } from 'mobx-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTabs,
  CardTitle,
} from 'components/Card'
import JobViewModel from '../JobViewModel'
import styles from './ResponsesCard.sass'
import { SectionError, SectionLoading } from '../Section/Section'
import { QuestionDTO } from 'jobs/dtos/questions'
import { QuestionViewModelFor } from 'jobs/questions/type-registry'
import ResponseValue from 'jobs/components/ResponseValue'
import { TabBar, TabItem } from '../../../../../components/TabBar'
import { ResponsesInformationTab } from '../DetailsView/DetailsViewModel'
import { match } from '../../../../../utils/pattern-match-util'
import { NotesTab } from '../CapturedInformationCard'
import QuestionListViewModel from '../../../../questions/QuestionListViewModel'
import BusinessInfo from 'jobs/info-gathering/business-info/components/BusinessInfo'
import PersonalProfile from 'jobs/info-gathering/individual-info/components/PersonalProfile'
import SpousalProfile from 'jobs/info-gathering/individual-info/components/SpousalProfile'
import DependentsProfile from 'jobs/info-gathering/individual-info/components/DependentsProfile'
import RefundInfo from 'jobs/info-gathering/tax-refund-method/components/RefundInfo'
import Pill from 'components/Pill'

interface ResponseItemProps<T extends QuestionDTO> {
  questionVM: QuestionViewModelFor<T>
}

function Response<T extends QuestionDTO>({ questionVM }: ResponseItemProps<T>) {
  return (
    <div className={styles.responseItem}>
      <div className={styles.responseItemLabel}>{questionVM.question}</div>
      <div className={styles.responseItemValue}>
        <ResponseValue vm={questionVM} />
      </div>
    </div>
  )
}

interface QuestionsTabProps {
  vm: QuestionListViewModel
}

export const QuestionsTab = observer(function QuestionsTab({
  vm,
}: QuestionsTabProps) {
  return (
    <div className={styles.questionTabRootContainer}>
      {vm.questions.map((question) => (
        <Response key={question.id} questionVM={question} />
      ))}
    </div>
  )
})

interface Props {
  vm: JobViewModel
}

export default observer(function ResponsesCard({ vm }: Props) {
  React.useEffect(() => {
    // Always show the responses by default when the component is first mounted
    vm.detailsVM.responsesSection.set(true)
  }, [vm.detailsVM.responsesSection])

  return (
    <Card unconstrained>
      <CardHeader compact bordered>
        <CardTitle cardLayout>
          <>Onboarding Information</>
          {vm.hasCompletedChecklistItem && (
            <div className={styles.newPill}>
              <Pill color="red">New</Pill>
            </div>
          )}
        </CardTitle>

        <CardTabs>
          <TabBar>
            <TabItem
              cardLayout
              active={
                vm.detailsVM.informationTabController.current.tab ===
                ResponsesInformationTab.Questions
              }
              onClick={() =>
                vm.detailsVM.informationTabController.change(
                  ResponsesInformationTab.Questions,
                )
              }
            >
              Questions (
              {vm.detailsVM.responses.questions.length || vm.job.answers.length}
              )
            </TabItem>

            {vm.notesVM.areNotesEnabled && (
              <TabItem
                active={
                  vm.detailsVM.informationTabController.current.tab ===
                  ResponsesInformationTab.Notes
                }
                onClick={() =>
                  vm.detailsVM.informationTabController.change(
                    ResponsesInformationTab.Notes,
                  )
                }
              >
                Notes
              </TabItem>
            )}

            {vm.personalProfileVM.personalInfoSupportingItem?.completed && (
              <TabItem
                cardLayout
                active={
                  vm.detailsVM.informationTabController.current.tab ===
                  ResponsesInformationTab.PersonalInfo
                }
                onClick={() =>
                  vm.detailsVM.informationTabController.change(
                    ResponsesInformationTab.PersonalInfo,
                  )
                }
                dataTest="personal-info-tab-item"
              >
                Personal
              </TabItem>
            )}

            {vm.businessInfoVM.businessSupportingItem?.completed && (
              <TabItem
                cardLayout
                active={
                  vm.detailsVM.informationTabController.current.tab ===
                  ResponsesInformationTab.BusinessInfo
                }
                onClick={() =>
                  vm.detailsVM.informationTabController.change(
                    ResponsesInformationTab.BusinessInfo,
                  )
                }
                dataTest="business-tab-item"
              >
                Business
              </TabItem>
            )}

            {vm.taxRefundMethodVM.isRelevant && (
              <TabItem
                cardLayout
                active={
                  vm.detailsVM.informationTabController.current.tab ===
                  ResponsesInformationTab.RefundInfo
                }
                onClick={() =>
                  vm.detailsVM.informationTabController.change(
                    ResponsesInformationTab.RefundInfo,
                  )
                }
                dataTest="refund-tab-item"
              >
                Refund
              </TabItem>
            )}
          </TabBar>
        </CardTabs>
      </CardHeader>
      <CardContent scroll listContent>
        {match(vm.detailsVM.informationTabController.current.tab, {
          [ResponsesInformationTab.Questions]: () =>
            vm.detailsVM.fetchResponses.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading responses." error={err} />
              ),
              resolved: () => <QuestionsTab vm={vm.detailsVM.responses} />,
            }),
          [ResponsesInformationTab.Notes]: () =>
            vm.fetchChecklist.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading checklist." error={err} />
              ),
              resolved: () => <NotesTab vm={vm.notesVM} />,
            }),
          [ResponsesInformationTab.PersonalInfo]: () =>
            vm.fetchChecklist.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading checklist." error={err} />
              ),
              resolved: () => (
                <>
                  <PersonalProfile vm={vm.personalProfileVM} />
                  <SpousalProfile vm={vm.spousalProfileVM} />
                  <DependentsProfile vm={vm.dependentProfileVM} />
                </>
              ),
            }),
          [ResponsesInformationTab.BusinessInfo]: () =>
            vm.fetchChecklist.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading checklist." error={err} />
              ),
              resolved: () => <BusinessInfo vm={vm.businessInfoVM} />,
            }),
          [ResponsesInformationTab.RefundInfo]: () =>
            vm.fetchChecklist.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading checklist." error={err} />
              ),
              resolved: () => <RefundInfo vm={vm.taxRefundMethodVM} />,
            }),
        })}
      </CardContent>
    </Card>
  )
})
